import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileDown, FileSpreadsheet, Filter } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useProject } from "@/pages/project/ProjectLayout";
import { useAuth } from "@/context/AuthContext";
import { useProjectSocket } from "@/hooks/useProjectSocket";
import { FilterBar } from "@/components/table/FilterBar";
import { PanelTable } from "@/components/table/PanelTable";

const NO_PRESETS = [];

const toggleSelection = (prev, uid, additive) => {
  if (!additive) return [uid];
  return prev.includes(uid) ? prev.filter((s) => s !== uid) : [...prev, uid];
};

export default function PanelListPage() {
  const { project, projectId } = useProject();
  const { capabilities } = useAuth();
  const [panels, setPanels] = useState([]);
  const [options, setOptions] = useState({});
  const [filters, setFilters] = useState({});
  const { isCompact } = useIsMobile();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selection, setSelection] = useState([]);
  const [exporting, setExporting] = useState(false);

  const download = async (kind) => {
    setExporting(true);
    try {
      const path = kind === "pdf" ? "panels.pdf" : "panels";
      const { data } = selection.length
        ? await api.post(
            `/projects/${projectId}/exports/${path}`,
            { panel_uids: selection },
            { responseType: "blob" }
          )
        : await api.get(`/projects/${projectId}/exports/${path}?scope=filtered&${query}`, {
            responseType: "blob",
          });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `panel_list.${kind === "pdf" ? "pdf" : "xlsx"}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setExporting(false);
    }
  };
  const [height, setHeight] = useState(600);

  const revisionId = project?.active_revision_id;

  const query = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    return params.toString();
  }, [filters]);

  const loadPanels = useCallback(async () => {
    if (!revisionId) return;
    try {
      const { data } = await api.get(`/projects/${projectId}/panels?${query}`);
      setPanels(data.panels);
    } catch (error) {
      toast.error(apiError(error));
    }
  }, [projectId, revisionId, query]);

  useEffect(() => {
    loadPanels();
  }, [loadPanels]);

  useEffect(() => {
    if (!revisionId) return;
    api
      .get(`/projects/${projectId}/panels/filter-options`)
      .then((res) => setOptions(res.data))
      .catch(() => {});
  }, [projectId, revisionId]);

  useEffect(() => {
    const resize = () => setHeight(Math.max(280, window.innerHeight - 190));
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  useProjectSocket(projectId, (message) => {
    if (["panel_status_changed", "pallet_assigned", "panel_glass_changed"].includes(message.event))
      loadPanels();
  });

  if (!revisionId)
    return (
      <div className="flex flex-1 items-center justify-center px-6" data-testid="panel-list-empty">
        <p className="text-sm text-slate-600">
          No active model revision — upload a Rhino model on the Model tab first.
        </p>
      </div>
    );

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="panel-list-page">
      {isCompact ? (
        <div className="flex shrink-0 items-center gap-2 border-b border-slate-200 bg-white px-3 py-2">
          <button
            data-testid="mobile-filters-button"
            onClick={() => setFiltersOpen((open) => !open)}
            className="mono flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-700"
          >
            <Filter size={12} /> Filters
          </button>
          <span className="mono text-[10px] uppercase tracking-wider text-slate-500">
            {panels.length} panels
          </span>
        </div>
      ) : null}

      {!isCompact || filtersOpen ? (
        <div className={isCompact ? "okn-scroll max-h-[45vh] overflow-y-auto" : ""}>
          <FilterBar
            filters={filters}
            setFilters={setFilters}
            options={options}
            onReset={() => setFilters({})}
            onSelectFiltered={() => setSelection(panels.map((p) => p.panel_uid))}
            matchCount={panels.length}
            presets={NO_PRESETS}
            onSavePreset={() => toast.info("Save presets from the Model tab")}
            onDeletePreset={() => {}}
          />
        </div>
      ) : null}

      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-3 py-1.5">
        {capabilities?.is_guest ? null : (
          <>
        <button
          data-testid="panel-list-export-excel"
          disabled={exporting}
          onClick={() => download("excel")}
          className="mono flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1 text-[10px] uppercase tracking-wider text-slate-600 hover:bg-slate-100 hover:text-emerald-700 disabled:opacity-50"
        >
          <FileSpreadsheet size={12} /> Export Excel
        </button>
        <button
          data-testid="panel-list-export-pdf"
          disabled={exporting}
          onClick={() => download("pdf")}
          className="mono flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1 text-[10px] uppercase tracking-wider text-slate-600 hover:bg-slate-100 hover:text-red-700 disabled:opacity-50"
        >
          <FileDown size={12} /> Export PDF
        </button>
          </>
        )}
        <span
          data-testid="export-scope-hint"
          className="mono text-[10px] uppercase tracking-wider text-slate-400"
        >
          {selection.length
            ? `${selection.length} selected`
            : Object.keys(filters).some((key) => filters[key])
            ? `${panels.length} filtered`
            : "full list"}
        </span>
        {/* Compact layout already shows the count in its own header row. */}
        <span className="mono ml-auto hidden text-[10px] uppercase tracking-wider text-slate-500 lg:inline">
          {panels.length} panels
        </span>
      </div>

      {selection.length > 0 ? (
        <div
          data-testid="selected-export-bar"
          className="flex shrink-0 flex-wrap items-center gap-2 border-b border-cyan-200 bg-cyan-50/70 px-3 py-1.5"
        >
          <span className="mono text-[10px] uppercase tracking-wider text-cyan-700">
            {selection.length} selected — exports use this selection
          </span>
          <button
            data-testid="clear-selection-button"
            onClick={() => setSelection([])}
            className="mono ml-auto text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-800"
          >
            Clear
          </button>
        </div>
      ) : null}

      <PanelTable
        panels={panels}
        selection={selection}
        onSelect={(uid, additive) => setSelection((prev) => toggleSelection(prev, uid, additive))}
        height={height}
      />
    </div>
  );
}
